import { jwtDecode } from 'jwt-decode';
import { globalConfig } from 'src/configuration/config';

/**
 * Embed auth — CHILD/consumer side of the iframe postMessage contract.
 *
 * This app runs embedded in an iframe inside wio.front (the parent). It does NOT
 * own a session; the parent relays the advisor's access token via postMessage.
 *
 * Contract (mirrors wio.front's report-embed handshake):
 *   - child → parent  { type: 'wio:embed-ready' }      on load, asks for the token
 *   - parent → child  { type: 'wio:auth', accessToken } parent relays the token
 *   - child → parent  { type: 'wio:auth-expired' }      on 401, asks for a fresh token
 *   - child → parent  { type: 'WW_SUITABILITY_TEST_COMPLETED', payload: { clientId, riskId } }
 *
 * Security: every inbound message is validated against `parentOrigin` (config)
 * AND `event.source === window.parent`. Outbound messages always target
 * `parentOrigin` (never '*'). If `parentOrigin` is empty we fail closed.
 */

interface AuthMessage {
  type: 'wio:auth';
  accessToken: string;
}

let currentToken: string | null = null;
let pendingResolvers: Array<(token: string) => void> = [];
const tokenListeners = new Set<(token: string | null) => void>();

const getParentOrigin = (): string => globalConfig.config.parentOrigin;

const isFromParent = (event: MessageEvent): boolean => {
  const origin = getParentOrigin();
  if (!origin) return false; // fail closed when not configured
  return event.origin === origin && event.source === window.parent;
};

const isAuthMessage = (data: unknown): data is AuthMessage => {
  if (typeof data !== 'object' || data === null) return false;
  const message = data as Partial<AuthMessage>;
  return message.type === 'wio:auth' && typeof message.accessToken === 'string';
};

const setToken = (token: string): void => {
  currentToken = token;

  const resolvers = pendingResolvers;
  pendingResolvers = [];
  resolvers.forEach((resolve) => resolve(token));

  tokenListeners.forEach((listener) => listener(token));
};

const postToParent = (message: Record<string, unknown>): void => {
  const origin = getParentOrigin();
  if (!origin) return;
  window.parent.postMessage(message, origin);
};

const handleMessage = (event: MessageEvent): void => {
  if (!isFromParent(event)) return;
  if (!isAuthMessage(event.data)) return;
  setToken(event.data.accessToken);
};

/** Current relayed token, or null if the parent has not sent it yet. */
export const getAccessToken = (): string | null => currentToken;

interface TokenClaims {
  CompanyId?: string | number;
  company_id?: string | number;
}

/**
 * Company id from a relayed token. wio.identity emits the `CompanyId` claim;
 * Keycloak emits `company_id`. Returns null if absent or unparseable.
 */
export const getCompanyIdFromToken = (token: string): string | null => {
  try {
    const claims = jwtDecode<TokenClaims>(token);
    const raw = claims.CompanyId ?? claims.company_id;
    return raw === undefined || raw === null ? null : String(raw).trim();
  } catch {
    return null;
  }
};

/** Resolves with the current token, or the next one the parent relays. */
export const waitForAccessToken = (): Promise<string> => {
  if (currentToken) return Promise.resolve(currentToken);
  return new Promise<string>((resolve) => {
    pendingResolvers.push(resolve);
  });
};

/** Tell the parent our token is stale; it refreshes and relays a fresh one. */
export const requestTokenRefresh = (): Promise<string> => {
  currentToken = null;
  postToParent({ type: 'wio:auth-expired' });
  return waitForAccessToken();
};

/** Notify the parent that the test was signed (mirrors the legacy contract). */
export const notifyTestCompleted = (clientId: string, riskId: number): void => {
  postToParent({
    type: 'WW_SUITABILITY_TEST_COMPLETED',
    payload: { clientId, riskId },
  });
};

/** Subscribe to token changes. Returns an unsubscribe fn. */
export const subscribeToToken = (
  listener: (token: string | null) => void,
): (() => void) => {
  tokenListeners.add(listener);
  return () => {
    tokenListeners.delete(listener);
  };
};

/** Attach the listener and signal readiness so the parent relays the token. */
export const embedAuthInit = (): void => {
  window.addEventListener('message', handleMessage);
  postToParent({ type: 'wio:embed-ready' });
};
